import React, { FC, useState } from 'react'
import t from 'src/translations/main'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import useForm from 'src/components/Core/Hooks/useForm/useForm'
import utils, { convertDate } from 'src/components/Organisms/Form/utils'
import config from './config'
import updateModal from 'src/components/Sections/Modal/updateModal'
import { parseJson } from 'src/views/Pages/Onboarding/FormWrapper'
import { useDirectorsDetails, useDirectorsDetailsUpdate, useDropdownOptions } from 'src/components/Core/Context/OnboardingContext'

import Button from 'src/components/Atoms/Button/Button'
import Spinner from 'src/components/Core/Loading/Spinner/Spinner'
import Popup from 'src/components/Atoms/Popup/Popup'

import {
  FormStyle,
  FormFooter,
  FormFooterActionsRight,
} from 'src/components/Organisms/Form/Form.styles'

import { ApiData, TableObj } from 'src/views/Pages/Onboarding/types'
import { ModalDirectorDetailsProps } from './types'

const ModalDirectorDetailsForm: FC<ModalDirectorDetailsProps> = props => {
  const { actionType, applicationId = '' } = props
  const [serverValidationError, setServerValidationError] = useState('')
  const { isLoading, error, sendRequest } = useFetch()
  const tableData = useDirectorsDetails()
  const updateDirectorsTableData = useDirectorsDetailsUpdate()
  const { dropdownOptions } = useDropdownOptions()

  const getFetchedData = () => {
    const rowData = [...tableData].filter(
      tableDataObj => tableDataObj.id === props.id
    )
    return utils.transformTableObj(
      {
        ...rowData[0],
        col5: (dropdownOptions.countries.find(country => country.label === rowData[0].col5))?.index?.toString()
      },
      props.tableConfig
    )
  }

  const fetchedData = actionType === 'add' ? {} : getFetchedData()

  const submitForm = (submitData: ApiData) => {
    if (actionType === 'edit') submitData.data.id = fetchedData.id
    submitData.data.applicationId = applicationId

    // eslint-disable-next-line
    const modifyResponse = (data: any) => {
      const serverValidationErrorsParsed = parseJson(data?.reasonPhrase)
      if(serverValidationErrorsParsed && serverValidationErrorsParsed[0].key === 'generalError') {
        setServerValidationError(t(`onboarding.errors.${serverValidationErrorsParsed[0].errorcode}`) || serverValidationErrorsParsed[0].errordescription?.split(':')[1] || t('error.invalidValue'))
        return
      }
      if(Array.isArray(serverValidationErrorsParsed) && serverValidationErrorsParsed.length > 0) {
        submitData.setConfigData(utils.injectErrorPropToConfig(submitData.configData, [], serverValidationErrorsParsed))
        return
      }

      if (data) {
        updateModal({ modalState: 'close' })
        if (actionType === 'add') {
          submitData.data.id = data
          updateDirectorsTableData((prevState: TableObj[]) => {
            return utils.addRowToTableData(
              [...prevState],
              {...submitData.data, directorCountry: dropdownOptions.countries?.find(country => country?.index?.toString() === submitData?.data?.directorCountry)?.label},
              props.tableConfig,
              props.action
            )
          })
        }
        if (actionType === 'edit') {
          updateDirectorsTableData((prevState: TableObj[]) => {
            submitData.data.dateOfBirth = convertDate(submitData.data.dateOfBirth, true)
            return utils.getEditedTableData(
              [...prevState],
              {...submitData.data, directorCountry: dropdownOptions.countries?.find(country => country?.index?.toString() === submitData?.data?.directorCountry)?.label},
              props.tableConfig,
              props.action
            )
          })
        }
      }
    }
    sendRequest(
      {
        scope: 'onboarding',
        url: props.postUrl,
        method: 'POST',
        body: submitData.data,
      },
      modifyResponse
    )
  }

  const {
    formData,
    emptyValidation,
    handleInputChange,
    handleSubmit,
    configData
  } = useForm(
    fetchedData,
    config({ countries: dropdownOptions.countries }),
    submitForm
  )

  if (isLoading) return <Spinner removeFullscreen />

  if (error) return <p>{error?.message || error}</p>

  return (
    <FormStyle onSubmit={handleSubmit}>
      {utils.sectionBuilder(configData, formData, handleInputChange)}
      <FormFooter insideModal>
        <FormFooterActionsRight>
          <Button
            version={2}
            label={t('onboarding.main.cancelButton')}
            onClick={() => updateModal({ modalState: 'close' })}
          />
          <Button
            type='submit'
            label={t('onboarding.main.saveButton')}
            disabled={emptyValidation}
          />
        </FormFooterActionsRight>
      </FormFooter>
      {!isLoading && (error || serverValidationError) && <Popup type='error' label={error || serverValidationError} popupContainerWidth='auto' popupContainerHeight='auto'/>}
    </FormStyle>
  )
}

export default ModalDirectorDetailsForm
